let placesLibraryPromise = null;

// Places API (New) requires "Places API (New)" plus "Maps JavaScript API" to be
// enabled on the project. `importLibrary` works whichever loading strategy the
// Maps JavaScript API script in public/index.html was loaded with.
export const loadPlacesLibrary = () => {
  if (!placesLibraryPromise) {
    placesLibraryPromise = window.google?.maps?.importLibrary
      ? window.google.maps.importLibrary("places")
      : Promise.resolve(window.google?.maps?.places ?? null);
  }
  return placesLibraryPromise;
};

export const newSessionToken = async () => {
  const library = await loadPlacesLibrary();
  return library ? new library.AutocompleteSessionToken() : null;
};

// `geocode` is accepted but matches nothing as a primary type, so address
// searches are restricted to the granular address types instead.
export const ADDRESS_PRIMARY_TYPES = [
  "street_address",
  "premise",
  "subpremise",
  "route",
];

export const ESTABLISHMENT_PRIMARY_TYPES = ["establishment"];

export const fetchAutocompleteSuggestions = async ({
  input,
  sessionToken,
  includedPrimaryTypes,
  locationBias,
}) => {
  const library = await loadPlacesLibrary();
  if (!library) return [];
  const { suggestions } =
    await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      sessionToken,
      includedPrimaryTypes,
      includedRegionCodes: ["gb"],
      locationBias: locationBias ?? "IP_BIAS",
      language: "en",
    });
  return suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter(Boolean);
};

// Only Essentials fields are requested so the selection stays on the base SKU.
export const fetchSelectedPlace = async (placePrediction) => {
  const place = placePrediction.toPlace();
  await place.fetchFields({
    fields: ["displayName", "addressComponents", "formattedAddress"],
  });
  return place;
};

const pickComponent = (components, types, key = "longText") => {
  const match = components.find((component) =>
    types.some((type) => component.types.includes(type)),
  );
  return match?.[key] ?? "";
};

// Helper function to extract address components
export const extractAddressParts = (components) => {
  const streetNumber = pickComponent(components, ["street_number"]);
  const route = pickComponent(components, ["route"]);
  const premise = pickComponent(components, ["premise"]);
  const subpremise = pickComponent(components, ["subpremise"]);
  const locality = pickComponent(components, ["locality"]);
  const town = pickComponent(components, ["postal_town"]) || locality;
  const postcode = pickComponent(components, ["postal_code"]);
  const county = pickComponent(components, ["administrative_area_level_2"]);

  let addressLine1 = [streetNumber, route].filter(Boolean).join(" ");
  if (addressLine1 && subpremise) {
    addressLine1 += `, ${subpremise}`;
  }

  let addressLine2 =
    pickComponent(components, [
      "sublocality_level_1",
      "sublocality",
      "neighborhood",
    ]) || (locality && locality !== town ? locality : "");

  if (addressLine1 === "") {
    if (subpremise || premise) {
      addressLine1 = subpremise || premise;
    } else if (addressLine2) {
      addressLine1 = addressLine2;
      addressLine2 = "";
    } else if (town) {
      addressLine1 = town;
    }
  }
  return { addressLine1, addressLine2, town, postcode, county };
};

export const addressPartsFromPlace = (place) => {
  const parts = extractAddressParts(place?.addressComponents || []);
  if (!parts.addressLine1 && place?.formattedAddress) {
    return { ...parts, addressLine1: place.formattedAddress };
  }
  return parts;
};
